import {
  UnpackDashboardLinkDocument,
  type UnpackDashboardLinkQuery,
} from '@components/Shortlink/hooks/UnpackDashboardShortLink.graphql';
import useOnce from '@hooks/use-once';
import { logger } from '@modules/logger';
import {
  parseAndCachePrefilledCardData,
  parseAndCacheReferralTrackingData,
} from '@modules/referral-helpers';
import { getFeatureVariant } from '@modules/server-utils/feature-flags';
import { useUserProfile } from '@modules/user-profile/UserProfileProvider';
import { getUserHomePageRoute } from '@modules/user-profile/user-profile-helpers';
import { getUserSessionFromContext } from '@modules/user-profile/user-profile-session-encode';
import { apolloClient } from '@services/apollo';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import type { FC } from 'react';

const MainPageRedirect: FC<React.PropsWithChildren<unknown>> = () => {
  const { query, push, isReady } = useRouter();
  const { userProfileDetails } = useUserProfile();

  useOnce(
    (preventSecondRun) => {
      if (!isReady) {
        return;
      }

      if (query._dl === 'shareCard') {
        parseAndCachePrefilledCardData(query);
        parseAndCacheReferralTrackingData(query);
        push(`/onboarding/signup-redirect?opaque_token=${query['opaque_token']}`);
        preventSecondRun();
        logger.debug('Handled main page redirect via _dl=shareCard', {
          query,

          opaque_token_exists: !!query['opaque_token'],
        });
        return;
      } else {
        // Preserve welcomeApp=1 (set by the post-signup relay) so the
        // app-download experience still fires when home is /cards/personal.
        const home = getUserHomePageRoute(userProfileDetails);
        push(query.welcomeApp === '1' ? `${home}?welcomeApp=1` : home);
      }
    },
    [isReady, push, query._dl],
  );

  return null;
};

export default MainPageRedirect;

// Handling dashboard short-link redirects in the form of "{dashboard_root}/?qls={cuid}"

interface Props {
  error: string | null;
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const { query } = ctx;
  const qls = query.qls;

  if (typeof qls === 'undefined') {
    try {
      const session = await getUserSessionFromContext(ctx);

      if (session) {
        const { workspaceType, role } = session;

        const isMemberOfTeamWorkspace = workspaceType === 'TEAM' && role === 'MEMBER';
        const isOwnerOfIndividualWorkspace = workspaceType === 'INDIVIDUAL' && role === 'OWNER';

        if (isMemberOfTeamWorkspace || isOwnerOfIndividualWorkspace) {
          const { email, userId } = session;

          const flagArgs = {
            req: ctx.req,
            userId: userId,
            userProperties: {
              email: email || '',
            },
            workspaceId: session.workspaceId,
          };

          const [phase1Flag, phase2Flag] = await Promise.all([
            getFeatureVariant('web-unified-profile-phase-1', 'off', flagArgs),
            getFeatureVariant('web-unified-profile-phase-2', 'off', flagArgs),
          ]);

          const shouldRedirectToIndividualHome =
            (isMemberOfTeamWorkspace && phase2Flag === 'on') ||
            (isOwnerOfIndividualWorkspace && phase1Flag === 'on');

          if (shouldRedirectToIndividualHome) {
            return {
              redirect: {
                destination: `/${session.workspaceId}/dash`,
                permanent: false,
              },
            };
          }
        }
      }
    } catch {
      // Session decode can throw Unauthorized — fall through to client-side redirect
    }

    return {
      props: {
        error: null,
      },
    };
  }

  // http://localhost:3000/?qls=clwedqvsz007swdhvd0grs2qp

  if (typeof qls !== 'string') {
    return {
      props: {
        error: 'Unexpected argument format for dashboard short link',
      },
    };
  }

  try {
    const result = await apolloClient.query<UnpackDashboardLinkQuery>({
      query: UnpackDashboardLinkDocument,
      variables: {
        input: { qls: qls },
      },
    });

    if (result.data?.resolveDashboardShortLink == null) {
      return {
        props: {
          error: 'Unexpected error retrieving shortlink from query',
        },
      };
    }

    const url = new URL(result.data.resolveDashboardShortLink.unpacked_url);

    return {
      redirect: {
        destination: url.toString(),
        permanent: false,
      },
    };
  } catch (error) {
    logger.debug('Error in query: ', { error });
    return {
      props: {
        error: 'Unexpected error retrieving shortlink from query',
      },
    };
  }
};
